<template>
	<div>
		<div
			v-if="categoryName"
			class="category-title"
			:class="{ 'no-description': !categoryDescription }"
		>
			{{ categoryName }}
		</div>
		<p v-if="categoryDescription" class="category-description">
			{{ categoryDescription }}
		</p>
		<div v-if="formattedProducts.length" class="products-wrap">
			<ProductsList
				v-if="isListProductsView"
				:products="formattedProducts"
				:switcher="trending ? 'trending' : 'regular'"
			/>
			<ProductsTiles
				v-else
				:products="formattedProducts"
				:switcher="trending ? 'trending' : 'regular'"
			/>
		</div>
		<products-placeholder v-else />
	</div>
</template>

<script>
import { mapGetters } from 'vuex'

import { PRODUCTS_VIEW } from '@/constants'

import ProductsList from '../components/ProductsList'
import ProductsTiles from '../components/ProductsTiles'
import ProductsPlaceholder from '@/components/ProductsPlaceholder.vue'

export default {
	name: 'ProductsByCategory',

	components: {
		ProductsPlaceholder,
		ProductsList,
		ProductsTiles
	},

	props: {
		categoryName: {
			type: String,
			default: ''
		}
	},

	computed: {
		...mapGetters('productCatalog', [
			'productsOf',
			'categoryByName',
			'lastValidProductSearch'
		]),

		isListProductsView() {
			return (
				this.categoryByName(this.categoryName).viewItem === PRODUCTS_VIEW.list
			)
		},

		categoryDescription() {
			return this.categoryByName(this.categoryName)?.description || ''
		},

		productsByCategory() {
			return this.productsOf(this.categoryName) || []
		},

		formattedProducts() {
			if (!this.lastValidProductSearch) return this.productsByCategory
			const searchChars = this.lastValidProductSearch.split('')
			return this.productsByCategory.map(product => {
				const { name } = product
				const nameChars = (name || '').split('')
				const formattedNameChars = [...nameChars]
				let prevCharIndex = null
				searchChars.forEach(searchChar => {
					let isReplaced = false
					nameChars.forEach((nameChar, index) => {
						if (
							isReplaced ||
							(prevCharIndex !== null && index <= prevCharIndex)
						)
							return
						// Means that current char of the name has already been replaced
						const isReplacedOnPrevSearchChar = String(
							formattedNameChars[index]
						).includes('span')
						if (
							searchChar.toLowerCase() === nameChar.toLowerCase() &&
							!isReplacedOnPrevSearchChar
						) {
							if (nameChar === '<') {
								formattedNameChars[index] =
									'<span class="highlighted-product-name">&lt;</span>'
								isReplaced = true
								prevCharIndex = index
								return
							}
							if (nameChar === '>') {
								formattedNameChars[index] =
									'<span class="highlighted-product-name">&gt;</span>'
								isReplaced = true
								prevCharIndex = index
								return
							}
							formattedNameChars[
								index
							] = `<span class="highlighted-product-name">${nameChar}</span>`
							isReplaced = true
							prevCharIndex = index
						}
						if (!isReplaced) {
							if (nameChar === '<') {
								formattedNameChars[index] = '&lt;'
							}
							if (nameChar === '>') {
								formattedNameChars[index] = '&gt;'
							}
						}
					})
				})
				return {
					...product,
					name: formattedNameChars.join('')
				}
			})
		},

		trending() {
			return +this.$route.query.trending
		}
	}
}
</script>

<style lang="scss">
@import './src/assets/styles/variables';

.products-wrap {
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	min-height: 100%;
	width: 100%;
	position: relative;
}

.category-title {
	font-size: 26px;
	font-weight: 700;
	text-transform: capitalize;
	color: $white;
	margin: 0 0 6px 0;
	overflow: hidden;
	text-overflow: ellipsis;

	@media (min-width: 1024px) {
		font-size: 40px;
	}

	&.no-description {
		margin: 0 15px 15px 0;

		@media (min-width: 768px) {
			margin: 0 0 16px;
		}

		@media (min-width: 1360px) {
			margin-bottom: 25px;
		}
	}
}

.category-description {
	color: $text-color;
	margin: 0 0 16px;

	@media (min-width: 1360px) {
		margin: 0 0 26px;
	}
}
</style>
